import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Grid,
  InputAdornment,
  makeStyles,
} from '@material-ui/core';
import { ArrowForwardIos } from '@material-ui/icons';
import styled from 'styled-components';
import configuration from '../../../../configs/configuration';
import { useSite } from '../../../../context/siteContext';
import { regexAlphabetFormat, validateEmail } from '../../../../utils/utils';
import { MY_COUNTRY_CODE } from '../../../../utils/constant';
import {
  DEFAULT_PHONE_NUMBER_LENGTH,
  MY_PHONE_NUMBER_LENGTH,
  validatePhoneNumber,
  validateThaiNumber,
} from '../../../../utils/Validator/PhonNumber';
import useContractInfo, {
  useUpdateShippingAddress,
  useUpdateCustomerEmail,
  useUpdateCartEmail,
} from '../../../ContractInfo/hook/useContractInfo';
import { saveEmail } from '../../../../store/authentication/slices';
import { useLanguage } from '../../../../context/language/language';
import useResponsive from '../../../../utils/responsive';
import { Button, InputField } from '../../../common';
import PhoneCode from '../../../common/Form/PhoneCode';
import Layout from '../Layout';
import useDeliveryData from '../../../common/hook/useDeliveryData';
import useDeliveryControlFlow from '../../../common/hook/useDeliveryControlFlow';
import { selectUserProfile } from '../../../../store/customer/selector';
import {
  DELIVERY_ADDRESS,
  SELECT_STORE,
  CUSTOMER_ADDRESS
} from '../../../../constant/deliveryFlow';
import { defaultModalSize, mobileContentContainerHeight } from '../modalSize';
import { getImageURL } from '../../../../utils/getImageURL';
import { trackPageLoad } from '../../../../utils/adobeAnalytics';
import useAuthentication from '../../../common/hook/useAuthentication';
import CrossIconButton from '../../../common/Button/CrossIconButton';
import { trackClickCheckoutContactInto } from '../../../../utils/eventTracking';
import { regexThaiAlphabetAndEnglishAlphabetAndSpaces, regexThaiAlphabetWithNumeric } from '../../../../utils/function';

const Placelocation = getImageURL('/icon/placelocation.svg');
const Storelocation = getImageURL('/icon/storelocation.svg');

const useStyle = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      paddingTop: 0,
    },

    [theme.breakpoints.up('lg')]: {
      padding: 0,
    },
    [theme.breakpoints.down('xs')]: {
      padding: '0 20px'
    },
  },
  buttonWrapperRight: {
    [theme.breakpoints.up('xs')]: {
      paddingRight: 6,
    },
  },
  buttonWrapperLeft: {
    [theme.breakpoints.up('xs')]: {
      paddingLeft: 6,
    },
  },
  inputEmail: {
    [theme.breakpoints.down('xs')]: {
      paddingBottom: 16
    }
  },
  recipientBox: {
    [theme.breakpoints.down('xs')]: {
      marginTop: 16
    }
  },
  mobileField: {
    padding: '29.39px 12px 10px'
  }
}));

const Main = styled.div(({ theme, width }) => ({
  width,
  padding: theme.isTabletOrMobile ? 0 : 5,
  overflow: theme.isTabletOrMobile ? 'auto' : 'initial',
  '&::-webkit-scrollbar-track': {
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
  },
  '&::-webkit-scrollbar': {
    width: 4,
    backgroundColor: '#FFFFFF',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: '#E8E8E8',
  },
}));

const Title = styled.div(() => ({
  overflow: 'hidden',
  width: '100%',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  textOverflow: 'ellipsis',
}));

const Label = styled.div(({ theme }) => ({
  ...theme.fonts.header5,
  paddingBottom: 4,
}));

const SubLabel = styled.div(({ theme }) => ({
  ...theme.fonts.textS,
  color: theme.colors.grey550,
  paddingBottom: 12,
}));

const AddressInfo = styled.div(({ theme }) => ({
  ...theme.radius.m,
  ...theme.fonts.textM,
  background: theme.colors.grey25,
  minHeight: '48px',
  height: 'auto',
  border: '1px solid #E0E0E0',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  marginTop: 8,
  padding: '4px 12px',
  ...theme.isMobile && {
    padding: '8px 12px',
  }
}));

const PlaceIcon = styled.div(({ theme }) => ({
  display: 'flex',

  ...theme.isTabletOrMobile && {
    paddingTop: 4,
    alignSelf: 'flex-start',
  }
}));

const StoreIcon = styled.div(({ theme }) => ({
  display: 'flex',
  paddingTop: 4,
  alignSelf: 'flex-start',
}));

const Icon = styled.img(({ w, h }) => ({
  width: w,
  height: h
}));

const Content = styled.div(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  flexWrap: 'wrap',
  cursor: 'pointer',
  flexGrow: 1,
  padding: '0 8px',
  WebkitLineClamp: theme.isTabletOrMobile ? 3 : 2,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}));

const StoreLabel = styled.div(({ theme }) => ({
  padding: '0 8px',
  ...theme.fonts.header6,
}));

const ButtonGroup = styled(Grid)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  bottom: 0,
  backgroundColor: 'white',
  zIndex: 10,
  padding: '24px 12px',
  gap: 14,

  ...theme.isMobile && {
    borderTopWidth: 0.5,
    borderTopStyle: 'solid',
    borderTopColor: theme.colors.grey100,
    padding: '12px 12px 8px 12px',
    gap: 0
  },
}));

const CustomButton = styled(Button)(({ theme }) => ({
  ...theme.fonts.header5,
}));

export default function SummaryContact({
  onNext,
  onBack,
  onClose,
  modalRef,
}) {
  const dispatch = useDispatch();
  const word = useLanguage();
  const classes = useStyle();
  const { MySite } = useSite();

  const [form, setForm] = useState({
    recipientName: '',
    contactNo: '',
    deliveryNote: '',
    contactEmail: ''
  });
  const [errors, setErrors] = useState({
    recipientName: false,
    contactNo: false,
    deliveryNote: false,
    contactEmail: false
  });
  const [countryCode, setCountryCode] = useState('+60');

  const deliveryData = useDeliveryData();
  const deliveryControl = useDeliveryControlFlow();
  const { isMobile, isTablet, isTabletOrMobile } = useResponsive();
  const {
    updateShipping,
    resetUpdate: resetUpdateShiping,
    isSuccess: updateShippingAddressSuccess,
    isLoading: updateShippingAddressLoading,
  } = useUpdateShippingAddress();
  const {
    updateCustomer,
    resetUpdate: resetUpdateEmail,
    isSuccess: updateEmailSuccess,
    isLoading: updateCustomerEmailLoading,
  } = useUpdateCustomerEmail();
  const {
    updateCartEmail,
    resetUpdateCartEmail,
    isSuccess: updateCartEmailSuccess,
    isLoading: updateCartEmailLoading,
  } = useUpdateCartEmail();

  const customerProfile = useSelector(selectUserProfile);
  const { isLoggedIn, customerProfile: customerProfileAuth } = useAuthentication();
  const emailProfile = customerProfileAuth.customerId === 'GUEST'
    ? _.get(customerProfileAuth, 'email', '')
    : _.get(customerProfile, 'email', '');

  const siteCode = configuration.siteCode;
  const isCCMethod = deliveryData.activeDeliveryMethod.includes('CC');
  const isODType = deliveryData.activeDeliveryType.includes('OD');
  const storeType = deliveryData?.activeStore?.storeType;

  const setValidate = (name, val) => {
    if (_.isEmpty(val)) {
      setErrors({ ...errors, [name]: false });
    } else {
      setErrors({ ...errors, [name]: val });
    }
  };

  const resetInput = (name) => {
    setForm({
      ...form,
      [name]: ''
    });
  };

  const handleCountryCodeChange = (val) => {
    setForm({
      ...form,
      contactNo: ''
    });
    setValidate('contactNo', null);
    setCountryCode(val);
  };

  const handleChangeValue = (e) => {
    if (e.target.name === 'recipientName') {
      const re = regexThaiAlphabetAndEnglishAlphabetAndSpaces;

      if (_.isEmpty(e.target.value)) {
        setValidate(e.target.name, word('Contact.Error.PleaseEnterYourName'));
      } else {
        setForm({ ...form, [e.target.name]: e.target.value.replace(re, '') });
        setValidate(e.target.name, null);
        return;
      }
    }

    if (e.target.name === 'contactNo') {
      let validateMobile = false;
      if (!MySite) {
        validateMobile = validateThaiNumber(e.target.value);
      } else {
        validateMobile = validatePhoneNumber(e.target.value, countryCode, siteCode);
      }

      if (_.isEmpty(e.target.value)) {
        setValidate(e.target.name, word('General.Error.Empty.Mobile'));
      } else if (!validateMobile) {
        setValidate(e.target.name, word('Contact.Phone.ErrorText'));
      } else {
        setValidate(e.target.name, null);
      }
    }

    if (e.target.name === 'deliveryNote') {
      const re = regexThaiAlphabetWithNumeric;
      
      setForm({ ...form, [e.target.name]: e.target.value.replace(re, '') });
      setValidate(e.target.name, null);
      return;
    }

    if (e.target.name === 'contactEmail') {
      if (_.isEmpty(e.target.value)) {
        setValidate(e.target.name, word('Contact.Error.PleaseEnterYourEmailAddress'));
      } else if (!validateEmail(e.target.value)) {
        setValidate(e.target.name, word('Contact.Error.PleaseEnterValidEmail'));
      } else {
        setValidate(e.target.name, null);
      }
    }

    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleChangeAddess = () => {
    if (isODType) {
      deliveryData.updateForm('filter', { storeType });
    }
    if (isCCMethod) {
      onNext({ data: { latitude: deliveryData.activeStore.latitude, longitude: deliveryData.activeStore.longitude }, page: SELECT_STORE });
    } else {
      onNext({ page: isLoggedIn ? CUSTOMER_ADDRESS : DELIVERY_ADDRESS });
    }
  };

  const handleConfirm = async () => {
    let _errors = {
      recipientName: false,
      contactNo: false,
      deliveryNote: false,
      contactEmail: false
    };

    if (_.isEmpty(form.recipientName)) {
      _errors.recipientName = word('Contact.Error.PleaseEnterYourName');
    } else {
      const re = new RegExp(
        regexAlphabetFormat,
        'g',
      );
      if (!re.test(form.recipientName)) {
        _errors.recipientName = word('Contact.Address.ErrorText');
      }
    }
    if (_.isEmpty(form.contactNo)) {
      _errors.contactNo = word('General.Error.Empty.Mobile');
    } else {
      let validateMobile = false;
      if (!MySite) {
        validateMobile = validateThaiNumber(form.contactNo);
      } else {
        validateMobile = validatePhoneNumber(form.contactNo, countryCode, siteCode);
      }
      if (!validateMobile) {
        _errors.contactNo = word('Contact.Phone.ErrorText');
      }
    }
    if (!_.isEmpty(form.deliveryNote)) {
      const re = new RegExp('^([ก-๙a-zA-Z๐-๙0-9\'"“\\-.,()/ ])+$', 'g');

      if (!re.test(form.deliveryNote)) {
        _errors.deliveryNote = word('Address.Delivery.Note.ErrorText');
      }
    }
    if (_.isEmpty(form.contactEmail)) {
      _errors.contactEmail = word('Contact.Error.PleaseEnterYourEmailAddress');
    } else {
      if (!validateEmail(form.contactEmail)) {
        _errors.contactEmail = word('Contact.Error.PleaseEnterValidEmail');
      }
    }

    setErrors({ ..._errors });

    if (!Object.keys(_errors).find((k) => !!_errors[k])) {
      deliveryControl.setLoading(true);
      let updateContactNo;
      if (!MySite) {
        updateContactNo = `${countryCode}${form.contactNo.slice(1)}`;
      } else {
        updateContactNo = `${countryCode}${form.contactNo}`;
      }
      await updateShipping({ ...form, contactEmail: undefined, contactNo: updateContactNo.replace('+', '') });

      if (isLoggedIn) {
        const customer = _.pick(customerProfile, _.identity);
        await updateCustomer({ ...customer, email: form.contactEmail });
      } else {
        await updateCartEmail({ email: form.contactEmail });
      }
      dispatch(saveEmail({ email: form.contactEmail }));
    }
    deliveryControl.setLoading(false);
    trackClickCheckoutContactInto();
  };

  const handleCancel = () => onClose();

  // useMemo(() => {
  //   if ([updateShippingAddressLoading, updateCustomerEmailLoading, updateCartEmailLoading].every((arr) => !arr)) {
  //     deliveryControl.setLoading(false);
  //   }
  // }, [updateShippingAddressLoading, updateCustomerEmailLoading, updateCartEmailLoading]);

  useEffect(() => {
    if (updateShippingAddressSuccess && (updateEmailSuccess || updateCartEmailSuccess)) {
      resetUpdateShiping();
      resetUpdateEmail();
      resetUpdateCartEmail();
      onClose();
    }
  }, [updateShippingAddressSuccess, updateEmailSuccess, updateCartEmailSuccess]);

  useEffect(() => {
    const formData = {
      recipientName: '',
      contactNo: '',
      deliveryNote: '',
      contactEmail: ''
    };

    const { shippingAddress } = deliveryData;
    const recipientName = _.get(shippingAddress, ['recipientName'], '');
    const contactNo = _.get(shippingAddress, ['contactNo'], '');
    const deliveryNote = _.get(shippingAddress, ['deliveryNote'], '');

    const _mobileNumber = (contactNo || customerProfile?.mobileNumber || '').replace('+', '');
    formData.recipientName = recipientName || customerProfile?.recipientName || '';
    formData.deliveryNote = deliveryNote || '';
    formData.contactEmail = emailProfile || '';

    if (MySite) {
      if (_mobileNumber?.startsWith('60') || _mobileNumber?.startsWith('65')) {
        setCountryCode(`+${_mobileNumber.substring(0, 2)}`);
        formData.contactNo = _mobileNumber.slice(2);
      } else {
        formData.contactNo = _mobileNumber;
      }
    } else {
      setCountryCode('+66');
      if (_mobileNumber?.startsWith('66')) {
        formData.contactNo = `0${_mobileNumber.slice(2)}`;
      } else {
        formData.contactNo = _mobileNumber;
      }
    }

    setForm(formData);
    trackPageLoad('Summary Contact');
  }, []);

  const contentContainerRef = useRef(null);
  useEffect(() => {
    mobileContentContainerHeight(contentContainerRef, isMobile, modalRef);
  }, [contentContainerRef]);

  return (
    <Layout
      title={(
        <Title>
          {word('OrderSummary.Contact.AddressContact')}
        </Title>
      )}
      onBack={onBack}
      backButtonShown={isMobile ? true : deliveryControl.stackSize > 1}
      onClose={isMobile ? false : onClose}
      width={isTablet ? defaultModalSize.tablet.width : defaultModalSize.desktop.width}
      height={isTablet ? defaultModalSize.tablet.height : defaultModalSize.desktop.height}
      scrollY
      isShowBorderBottom
    >
      <Container className={classes.root} ref={contentContainerRef}>
        <Main>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} style={{ ...isMobile && { paddingBottom: 0 } }}>
                <div className={classes.recipientBox}>
                  <InputField
                    label={word('Contact.Recipient.Name')}
                    id="recipientName"
                    name="recipientName"
                    fullWidth
                    value={form.recipientName}
                    onChange={handleChangeValue}
                    onFocus={handleChangeValue}
                    inputProps={{ maxLength: 100 }}
                    require
                    error={errors.recipientName}
                    errormessage={errors.recipientName}
                    InputProps={{
                      endAdornment: form.recipientName ? (
                        <InputAdornment style={{ marginLeft: 12 }} position="end">
                          <CrossIconButton marginRight={0} onClick={() => resetInput('recipientName')} />
                        </InputAdornment>
                      ) : (
                        ''
                      ),
                    }}
                  />
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                container
                direction="row"
                wrap="nowrap"
                style={{ ...isMobile && { paddingBottom: 0 } }}
              >
                {MySite && (
                <Box flexShrink={1}>
                  <PhoneCode
                    defaultValue={countryCode}
                    onChange={handleCountryCodeChange}
                  />
                </Box>
                )}
                <Box width="100%">
                  <InputField
                    label={word('Contact.MobileNumber')}
                    id="contactNo"
                    name="contactNo"
                    fullWidth
                    value={form.contactNo}
                    onChange={handleChangeValue}
                    onFocus={handleChangeValue}
                    inputProps={{
                      maxLength: MySite && countryCode === `+${MY_COUNTRY_CODE}`
                        ? MY_PHONE_NUMBER_LENGTH
                        : DEFAULT_PHONE_NUMBER_LENGTH
                    }}
                    require
                    error={errors.contactNo}
                    errormessage={errors.contactNo}
                    InputProps={{
                      classes: {
                        ...MySite && isMobile && {
                          input: classes.mobileField
                        }
                      },
                      endAdornment: form.contactNo ? (
                        <InputAdornment style={{ marginLeft: 12 }} position="end">
                          <CrossIconButton marginRight={0} onClick={() => resetInput('contactNo')} />
                        </InputAdornment>
                      ) : (
                        ''
                      ),
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <InputField
                  label={isCCMethod ? word('Address.Collect.Note') : word('Address.Delivery.Note')}
                  id="deliveryNote"
                  name="deliveryNote"
                  fullWidth
                  value={form.deliveryNote}
                  onChange={handleChangeValue}
                  onFocus={handleChangeValue}
                  inputProps={{ maxLength: 250 }}
                  multiline
                  minRows={3}
                  error={errors.deliveryNote}
                  errormessage={errors.deliveryNote}
                  InputProps={{
                    endAdornment: form.deliveryNote ? (
                      <InputAdornment style={{ marginLeft: 12 }} position="end">
                        <CrossIconButton marginRight={0} onClick={() => resetInput('deliveryNote')} />
                      </InputAdornment>
                    ) : (
                      ''
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} style={{ ...isMobile && { paddingTop: 10 } }}>
                <Label>{isCCMethod ? word('OrderSummary.Contact.StoreInfo') : word('OrderSummary.Contact.AddressInfo')}</Label>
                <AddressInfo onClick={handleChangeAddess}>
                  {isCCMethod ? (
                    <StoreIcon>
                      <Icon src={Storelocation} w={20} h={16} />
                    </StoreIcon>
                  ) : (
                    <PlaceIcon>
                      <Icon src={Placelocation} w={16} h={20} />
                    </PlaceIcon>

                  )}
                  {isCCMethod ? (
                    <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <StoreLabel>{deliveryData?.currentStore?.name || ''}</StoreLabel>
                      <Content>{deliveryData?.currentStore?.longAddress?.split('·')[1]?.trim() || ''}</Content>
                    </div>
                  ) : (<Content>{deliveryData?.activeAddressText || ''}</Content>)}
                  <ArrowForwardIos style={{ fontSize: 14 }} />
                </AddressInfo>
              </Grid>
              <Grid item xs={12} style={{ ...isMobile && { paddingTop: 10 } }}>
                <Label>{word('OrderSummary.Contact.Email')}</Label>
                <SubLabel>{word('OrderSummary.Contact.ConfirmEmailAddress.TextBody')}</SubLabel>
                <div className={classes.inputEmail}>
                  <InputField
                    label={word('OrderSummary.Contact.EmailAddress')}
                    id="contactEmail"
                    name="contactEmail"
                    fullWidth
                    value={form.contactEmail}
                    onChange={handleChangeValue}
                    onFocus={handleChangeValue}
                    inputProps={{ maxLength: 80 }}
                    require
                    error={errors.contactEmail}
                    errormessage={errors.contactEmail}
                    InputProps={{
                      endAdornment: form.contactEmail ? (
                        <InputAdornment style={{ marginLeft: 12 }} position="end">
                          <CrossIconButton marginRight={0} onClick={() => resetInput('contactEmail')} />
                        </InputAdornment>
                      ) : (
                        ''
                      ),
                    }}
                  />
                </div>
              </Grid>
            </Grid>
            <div style={{ height: isMobile ? 37 : 120 }} />
            <ButtonGroup container justify="center">
              <Grid item xs={6} md={4} sm={4} className={classes.buttonWrapperRight}>
                <CustomButton
                  id="cancel-contact-btn"
                  data-cta-name-en="Cancel"
                  data-cta-name-th="ยกเลิก"
                  data-cta-type="Button"
                  secondary
                  fullWidth
                  onClick={handleCancel}
                >
                  {word('OrderSummary.Contact.Cancel')}
                </CustomButton>

              </Grid>
              <Grid item xs={6} md={4} sm={4} className={classes.buttonWrapperLeft}>
                <CustomButton
                  id="confirm-contact-btn"
                  data-cta-name-en="Confirm"
                  data-cta-name-th="ยืนยัน"
                  data-cta-type="Button"
                  primary
                  fullWidth
                  onClick={handleConfirm}
                >
                  {word('OrderSummary.Contact.Confirm')}
                </CustomButton>
              </Grid>
            </ButtonGroup>
          </Box>
        </Main>
      </Container>
    </Layout>
  );
}
